import { Link } from 'react-router-dom';
import type { Article } from '@/types/news';
import { formatNumber, timeAgo } from '@/utils/news';

interface ArticleCardProps {
  article: Article;
  variant?: 'list' | 'grid' | 'compact';
  rank?: number;
}

export default function ArticleCard({ article, variant = 'list', rank }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex items-start gap-3 rounded-md py-2.5 transition-colors hover:bg-background-100"
      >
        {typeof rank === 'number' && (
          <span
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
              rank <= 3 ? 'bg-accent-500 text-background-50 dark:text-foreground-950' : 'bg-background-200 text-foreground-600'
            }`}
          >
            {rank}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-400">
            <span>{article.categoryName}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-500"></i>
              {formatNumber(article.hot)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'grid') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
      >
        <div className="relative h-44 w-full overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 热搜榜`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 rounded-md bg-primary-600/90 px-2 py-1 text-xs text-background-50">
            {article.categoryName}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-4">
          <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
          <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
            <span>{article.source}</span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {timeAgo(article.publishTime)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:flex-row sm:p-4"
    >
      <div className="h-44 w-full shrink-0 overflow-hidden rounded-md sm:h-32 sm:w-52">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 热搜榜`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-2 flex items-center gap-2">
          <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
            {article.categoryName}
          </span>
          {article.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
              #{tag}
            </span>
          ))}
        </div>
        <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600 md:text-lg">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {article.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {timeAgo(article.publishTime)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-accent-500"></i>
            {formatNumber(article.hot)}
          </span>
          <span className="hidden items-center gap-1 sm:flex">
            <i className="ri-eye-line"></i>
            {formatNumber(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}